"use client"

import { useEffect, useRef, useState } from "react"
import { Search } from "lucide-react"

import { Button } from "@/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/ui/dialog"

// Extend the Window interface to include PagefindUI
declare global {
  interface Window {
    PagefindUI: any
  }
}

export function PagefindSearchDialog() {
  const [open, setOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((open) => !open)
      }
    }

    document.addEventListener("keydown", down)
    return () => document.removeEventListener("keydown", down)
  }, [])

  useEffect(() => {
    if (open) {
      setIsLoading(true)

      // Wait for Pagefind to be available and element to be in DOM
      const initPagefind = () => {
        console.log("PagefindUI", window.PagefindUI)
        if (window.PagefindUI && document.getElementById("search")) {
          try {
            new window.PagefindUI({
              element: "#search",
              showSubResults: true,
              showImages: false,
              excerptLength: 20,
              pagefind_options: {
                baseUrl: "/",
              },
              processResult: (result: any) => {
                // Helper function to strip .html suffix while preserving hash fragments
                const stripHtmlSuffix = (url: string) => {
                  return url.replace(/\.html($|#)/, "$1")
                }

                // Process the main URL
                if (result.url) {
                  result.url = stripHtmlSuffix(result.url)
                }

                // Process URLs in sub-results
                if (result.sub_results) {
                  result.sub_results.forEach((subResult: any) => {
                    if (subResult.url) {
                      subResult.url = stripHtmlSuffix(subResult.url)
                    }
                  })
                }

                // If raw_url is present, process it too
                if (result.raw_url) {
                  result.raw_url = stripHtmlSuffix(result.raw_url)
                }

                // Process meta URL if present
                if (result.meta?.url) {
                  result.meta.url = stripHtmlSuffix(result.meta.url)
                }

                return result
              },
            })
            setIsLoading(false)
          } catch (error) {
            console.error("Failed to initialize Pagefind:", error)
            setIsLoading(false)
          }
        } else {
          // Retry after a short delay
          setTimeout(initPagefind, 100)
        }
      }

      // Small delay to ensure DOM is updated
      setTimeout(initPagefind, 50)
    }

    return () => {
      // Clean up Pagefind UI when dialog closes
      if (!open) {
        setIsLoading(false)
      }
    }
  }, [open])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="flex items-center gap-2"
          onClick={() => setOpen(true)}
        >
          <Search className="h-4 w-4" />
          <span className="hidden sm:inline">Search</span>
          <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100 sm:inline-flex">
            <span className="text-xs">⌘</span>K
          </kbd>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Search Content</DialogTitle>
          <DialogDescription>
            Search through all content on this website. Use keywords to find
            relevant articles and pages.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">Loading search...</div>
            </div>
          )}
          <div
            id="search"
            className="pagefind-search overflow-y-auto"
            style={{
              minHeight: "400px",
              maxHeight: "400px",
              display: isLoading ? "none" : "block",
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
